// Copyright 2025-2026 NXGN Management, LLC. All Rights Reserved.
import { useState, useCallback, MouseEvent } from 'react'
import {
  Accordion,
  Button,
  FileUpload,
  Flex,
  Typography,
  Modal,
  Note,
  UseShowHide,
} from '@teamnine/pcj-ui-v2'
import { unzip } from 'unzipit'
import { useQueryClient } from 'react-query'

import { submitObjectData, deployObjectData } from '@/services/templates'
import { useErrorHelper } from '@/app/shared/hooks/useErrorHelper'

type DeployDataModalProps = {
  modal: UseShowHide
  practiceId: string
}

export default function DeployDataModal({
  modal,
  practiceId,
}: DeployDataModalProps) {
  const queryClient = useQueryClient()
  const { basicErrorHandler, displayGlobalMessage } = useErrorHelper()

  const [files, setFiles] = useState<File[]>([])
  const [isUploading, setIsUploading] = useState(false)
  const [isDeploying, setIsDeploying] = useState(false)
  const [objectDataIds, setObjectDataIds] = useState<string[]>([])
  const [zippedFiles, setZippedFiles] = useState<string[]>([])
  const [attachments, setAttachments] = useState<string[]>([])

  const onDrop: Parameters<typeof FileUpload>[0]['onDrop'] = useCallback(
    async (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) return
      const file = acceptedFiles[0]
      setFiles([file])
      setObjectDataIds([])

      try {
        const { entries } = await unzip(file)
        const names = Object.keys(entries)
        setZippedFiles(
          names.filter(
            (name) => !name.endsWith('.zip') && !name.endsWith('.json')
          )
        )
        setAttachments(names.filter((name) => name.endsWith('.zip')))
      } catch (err) {
        console.error('Failed to unzip:', err)
        setZippedFiles([])
        setAttachments([])
      }
    },
    []
  )

  const onRemove = useCallback((index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index))
    setObjectDataIds([])
    setZippedFiles([])
    setAttachments([])
  }, [])

  const handleUpload = async () => {
    if (files.length === 0) return
    setIsUploading(true)
    try {
      const ods = await submitObjectData(files[0])
      const ids = ods.map((od: { id: string }) => od.id)
      setObjectDataIds(ids)
      displayGlobalMessage({
        heading: 'Object data uploaded successfully.',
        type: 'success',
      })
    } catch (err) {
      basicErrorHandler(err)
    } finally {
      setIsUploading(false)
    }
  }

  const handleDeploy = async () => {
    if (objectDataIds.length === 0) return
    setIsDeploying(true)
    try {
      await deployObjectData(practiceId, objectDataIds)
      displayGlobalMessage({
        heading: 'Tenant data successfully deployed.',
        type: 'success',
      })
      queryClient.invalidateQueries(['getTenant', practiceId])
      modal.hide()
      setFiles([])
      setObjectDataIds([])
      setZippedFiles([])
      setAttachments([])
    } catch (err) {
      basicErrorHandler(err)
    } finally {
      setIsDeploying(false)
    }
  }

  const hasUploaded = objectDataIds.length > 0

  return (
    <Modal open={modal.visible} onOpenChange={modal.toggle}>
      <Modal.Display aria-label="Deploy Data">
        <Modal.Header title="Deploy Data" />
        <Modal.Content>
          <Flex direction="column" gap={16}>
            <FileUpload
              multiple={false}
              maxFiles={1}
              isLocked={files.length > 0}
              onDrop={onDrop}
              accept={{ 'application/zip': ['.zip'] }}
            >
              {files.length === 0 ? (
                <Flex align="center" direction="column" gap={8}>
                  <Typography variant="16-medium">
                    Select a ZIP file containing object data, or click to browse
                  </Typography>
                  <Typography variant="14-regular" color="gray">
                    The file should contain valid object CSVs and optional
                    attachments.
                  </Typography>
                </Flex>
              ) : (
                files.map((file, index) => (
                  <Flex
                    key={index}
                    align="center"
                    justify="space-between"
                    gap={16}
                  >
                    <Typography variant="16-medium">{file.name}</Typography>
                    <Button
                      variant="blank"
                      onClick={(e: MouseEvent<HTMLButtonElement>) => {
                        e.stopPropagation()
                        onRemove(index)
                      }}
                    >
                      Remove
                    </Button>
                  </Flex>
                ))
              )}
            </FileUpload>
            {files.length > 0 &&
              (zippedFiles.length > 0 || attachments.length > 0) && (
                <Flex direction="column" gap={8}>
                  {zippedFiles.length > 0 && (
                    <Accordion title="Objects">
                      <Flex direction="column" gap={4}>
                        {zippedFiles.map((name) => (
                          <Typography key={name}>
                            {name.replace('.csv', '')}
                          </Typography>
                        ))}
                      </Flex>
                    </Accordion>
                  )}
                  {attachments.length > 0 && (
                    <Accordion title="Attachments">
                      <Flex direction="column" gap={4}>
                        {attachments.map((name) => (
                          <Typography key={name}>
                            {name.replace('_attachments.zip', '')}
                          </Typography>
                        ))}
                      </Flex>
                    </Accordion>
                  )}
                </Flex>
              )}
            {hasUploaded && (
              <Note color="success">
                Object data uploaded successfully. Click “Deploy” to upsert
                object data to the tenant.
              </Note>
            )}
          </Flex>
        </Modal.Content>
        <Modal.Footer
          footerAction={{
            primaryButton: hasUploaded
              ? {
                  label: 'Deploy',
                  onClick: handleDeploy,
                  loading: isDeploying,
                  disabled: isDeploying,
                }
              : {
                  label: 'Upload',
                  onClick: handleUpload,
                  loading: isUploading,
                  disabled: files.length === 0 || isUploading,
                },
            secondaryButton: {
              label: 'Cancel',
              closeModal: true,
              onClick: () => {
                setFiles([])
                setObjectDataIds([])
                setZippedFiles([])
                setAttachments([])
              },
            },
          }}
        />
      </Modal.Display>
    </Modal>
  )
}
